import React, { useMemo, useState } from 'react'
import { getFlagFallback, getFlagUrl } from '../../utils/countryFlags'

// Same region groupings used by the backend (api/api.py ::
// _process_data_viewer_groups_countries) so the tabs reflect real backend
// semantics instead of an invented grouping.
export const DATA_CENTER_REGIONS = {
  Andean: ['Bolivia', 'Colombia', 'Ecuador', 'Peru', 'Venezuela'],
  Southern_cone: ['Argentina', 'Brazil', 'Chile', 'Paraguay', 'Uruguay'],
  Central_america: ['Belize', 'Costa Rica', 'El Salvador', 'Guatemala', 'Honduras', 'Mexico', 'Nicaragua', 'Panama'],
  Caribbean: [
    'Antigua and Barbuda', 'The Bahamas', 'Bahamas', 'Barbados', 'Cuba', 'Dominica', 'Grenada', 'Haiti', 'Jamaica',
    'Dominican Republic', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Trinidad and Tobago',
  ],
}

const REGION_LABELS = {
  all: 'All regions',
  Andean: 'Andean',
  Southern_cone: 'Southern Cone',
  Central_america: 'Central America & Mexico',
  Caribbean: 'Caribbean',
}

function DataCenterCountryPicker({ countryList, selectedCountries, onToggle, onSelectAll, onClear }) {
  const [search, setSearch] = useState('')
  const [region, setRegion] = useState('all')

  const countries = useMemo(() => countryList || [], [countryList])

  const filteredCountries = useMemo(() => {
    return countries.filter((c) => {
      const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase())
      const matchesRegion = region === 'all' || (DATA_CENTER_REGIONS[region] || []).includes(c.name)
      return matchesSearch && matchesRegion
    })
  }, [countries, search, region])

  const allSelected = countries.length > 0 && selectedCountries.length === countries.length

  return (
    <div className="dc-panel" id="dcPanelCountries">
      <div className="dc-panel-head">
        <div className="dc-panel-head-left">
          <span className="dc-badge-step">1</span>
          <div>
            <h2 className="dc-panel-title">Countries</h2>
            <span className="dc-panel-sub">IDB borrowing countries in Latin America and the Caribbean</span>
          </div>
          <span className={`dc-count${selectedCountries.length === 0 ? ' is-empty' : ''}`}>
            {selectedCountries.length} selected
          </span>
        </div>
        <div className="dc-panel-tools">
          <button type="button" className="dc-tool" onClick={allSelected ? onClear : onSelectAll}>
            {allSelected ? 'Clear' : 'Select all'}
          </button>
        </div>
      </div>
      <div className="dc-panel-body">
        <div className="dc-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input type="search" placeholder="Search a country…" autoComplete="off" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="dc-subtabs">
          {Object.keys(REGION_LABELS).map((key) => (
            <button
              type="button"
              key={key}
              className={`dc-subtab${region === key ? ' is-on' : ''}`}
              onClick={() => setRegion(key)}
            >
              {REGION_LABELS[key]}
            </button>
          ))}
        </div>
        <div className="dc-country-grid">
          {filteredCountries.length === 0 && (
            <div className="dc-empty">No countries match your search.</div>
          )}
          {filteredCountries.map((c) => {
            const isOn = selectedCountries.includes(c.name)
            return (
              <div
                key={c.name}
                className={`dc-check${isOn ? ' is-on' : ''}`}
                onClick={() => onToggle(c.name)}
              >
                <input type="checkbox" readOnly checked={isOn} />
                <img
                  src={getFlagUrl(c.name)}
                  alt={`${c.name} flag`}
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none'
                    event.currentTarget.nextSibling.style.display = 'inline-flex'
                  }}
                />
                <span style={{ display: 'none' }}>{getFlagFallback(c.name)}</span>
                <span>{c.name}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default DataCenterCountryPicker
